
import { mapState } from 'vuex';
import RegistrationComponent from '@justeat/f-registration';
import metadataMixin from '@/mixins/metadataMixin';
import routingService from '@/services/utilities/routing.service';
import analyticsObjects from '@/services/analytics/account/register';
import { getPageName } from '@/services/analytics/pageData';
import { ANALYTICS_DATA_CREATED } from '~/constants/analytics';

const LOG_TAGS = 'registration';

export default {
    components: {
        RegistrationComponent
    },

    mixins: [metadataMixin],

    layout: 'noFooter',

    middleware: [
    ],

    data () {
        return {
            returnUrl: '/'
        };
    },

    computed: {
        ...mapState('configuration', [
            'settings'
        ]),

        ...mapState('logging', [
            'logBasePayload'
        ]),

        ...mapState('auth', [
            'authToken'
        ]),

        loginUrl () {
            const { baseLoginUrl } = this.settings.registration;

            return `${baseLoginUrl}?returnUrl=${encodeURIComponent(this.returnUrl)}`;
        }
    },

    async created () {
        this.returnUrl = this.$route.query.returnUrl || '/';
    },

    mounted () {
        this.$gtm.pushPageData({
            pageName: getPageName(this.$route, this.authToken)
        });

        this.$gtm.pushUserData({ authToken: this.authToken });

        this.$gtm.pushEvent({ event: ANALYTICS_DATA_CREATED });
    },

    methods: {
        handleCreateAccountSuccess (url) {
            this.publishCreateAccountSuccessEvents();

            routingService.redirectToUrl(url || this.returnUrl);
        },

        handleCreateAccountFailure (error) {
            const {
                isAxiosError,
                config,
                message,
                invalidFields,
                validFields
            } = error;

            this.$gtm.pushEvent(analyticsObjects.createAccountFormSubmitObject({
                changes: validFields
            }));

            if (isAxiosError && config) {
                this.$gtm.pushEvent(analyticsObjects.createAccountErrorObject({
                    conversationId: this.logBasePayload?.conversationId,
                    error: message
                }));

                this.$log.error(
                    `Consumer registration failure: ${message}`,
                    error,
                    LOG_TAGS,
                    {
                        endpointUrl: config.url,
                        endpointMethod: config.method
                    }
                );
            } else if (invalidFields) {
                this.$gtm.pushEvent(analyticsObjects.createAccountInlineErrorObject({
                    changes: validFields,
                    error: invalidFields
                }));

                this.$log.warn(
                    'Consumer registration failure: validation failed',
                    LOG_TAGS,
                    {
                        invalidFields,
                        validFields
                    }
                );
            } else {
                this.$log.error(
                    'Consumer registration failure: Unknown',
                    error,
                    LOG_TAGS,
                    { errorMessage: error.message }
                );
            }
        },

        handleCreateAccountWarning (error) {
            this.$gtm.pushEvent(analyticsObjects.createAccountFormSubmitObject({
                changes: error.validFields
            }));

            this.$gtm.pushEvent(analyticsObjects.createAccountErrorObject({
                conversationId: this.logBasePayload?.conversationId,
                error: error.message
            }));

            this.$log.warn(
                `Consumer registration failure: ${error.message}`,
                LOG_TAGS,
                { errorMessage: error.message }
            );
        },

        handleCreateAccountStart () {
            this.$gtm.pushEvent(analyticsObjects.createAccountFormStartObject());
        },

        handleVisitLoginPage () {
            this.$gtm.pushEvent(analyticsObjects.createAccountVisitLoginObject());
        },

        handleLoginBlocked () {
            this.$log.warn('Login blocked after successful registration', LOG_TAGS);

            const { loginPath } = this.settings.auth;

            const returnUrl = this.isEncoded(this.returnUrl) ? this.returnUrl : encodeURIComponent(this.returnUrl);

            // Adding a flag to the redirect so the user is shown a message to login again.
            const url = `${loginPath}?returnUrl=${returnUrl}&notification=true`;

            this.handleCreateAccountSuccess(url);
        },

        handleMfaChallengeIssued (mfaChallenge) {
            this.$log.warn(
                'MFA challenge issued after successful registration',
                LOG_TAGS
            );

            this.publishCreateAccountSuccessEvents();

            routingService.redirectToUrl(`/account/mfa?code=${encodeURIComponent(mfaChallenge.mfaToken)}&email=${encodeURIComponent(mfaChallenge.mfaTarget)}&returnUrl=${encodeURIComponent(this.returnUrl)}`);
        },

        handleRateLimit (error) {
            this.$log.warn(
                'CloudFlare rate limit exceeded',
                LOG_TAGS
            );
            this.$gtm.pushEvent(analyticsObjects.createAccountErrorObject({
                conversationId: this.logBasePayload?.conversationId,
                error: error.message
            }));
        },

        publishCreateAccountSuccessEvents () {
            this.$log.info('Account registered successfully', LOG_TAGS);

            this.$gtm.pushEvent(analyticsObjects.createAccountFormSubmitObject());

            this.$gtm.pushEvent(analyticsObjects.createAccountSuccessObject({
                conversationId: this.logBasePayload?.conversationId
            }));
        },

        isEncoded (str) {
            return typeof str === 'string' && decodeURIComponent(str) !== str;
        }
    }
};
